import './App.css';
import Home from './pages/Home';
import appContext from './AppContext';
import React, { Suspense, useState, useEffect } from 'react';
import { getAllSchools } from './services/ESService';
import { getAllRailStations } from './services/StationService';
import { createBrowserHistory } from 'history';
import { filterSortSchools } from './services/WranglingService';
import i18n from './i18n';
import Loading from './components/Loading';
import appConfig from './config/appConfig.json';
import ScrollTop from './components/ScrollTop';
import Apply from './components/Apply';

export const history = createBrowserHistory({
  basename: process.env.PUBLIC_URL
});

export default function App() {
  const [originSchools, setOriginSchools] = useState([]);
  const [schools, setSchools] = useState([]);
  const [stations, setStations] = useState({});
  const [gradeFilters, setGradeFilters] = useState([]);
  const [wardFilters, setWardFilters] = useState([]);
  const [offeringSorts, setOfferingSorts] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [searchedPlace, setSearchedPlace] = useState(null);
  const [inBoundaryFilter, setInBoundaryFilter] = useState(false);
  const [boundaryPolys, setBoundaryPolys] = useState([]);
  const [locale, setLocale] = useState("en");
  const [filterInit, setFilterInit ] = useState(false);
  
  const offeringLabels = appConfig.offerings;
  i18n.on('languageChanged', (lng) => setLocale(i18n.language));

  useEffect(() => {
    setLoadingData(true);
    // on load - get all stations
    getAllRailStations().then( data => {
      setStations(data);
    })
    .catch( error => {
      console.error("Error in searching all rail stations", error);
    })

    // check query parameters and preset filtered values
    const params = new URL(window.location).searchParams;
    const paramAddress = params.get('addr');
    const paramGrades = params.get('selGrades');
    const paramWards = params.get('selWards');
    const paramOfferings = params.get('programs');
    let filters = setFilters(paramGrades, paramWards, paramOfferings, paramAddress);

    // once filters set - start loading data
    if (paramAddress) {
      // if address from URL - this will be handled by MARInput.js file
      setFilterInit(true);
    } else {
      getAllSchools().then( data => {
        setOriginSchools([...data]);
        // set default schools if no address
        setSchools(filterSortSchools(data, filters[0], filters[1], filters[2], filters[3]));
        setFilterInit(true);
      }).catch( error => {
        console.error("Error in searching all schools", error);
      }).finally(()=>{
        setLoadingData(false);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // if already initialized - set query parameters on change
    if (filterInit && originSchools.length > 0) {
      const url = new URL(window.location);
      // set address params
      if (searchedPlace !== null) {
        url.searchParams.set('addr', searchedPlace.properties.FullAddress);
        url.searchParams.set('lat', searchedPlace.properties.Latitude);
        url.searchParams.set('lng', searchedPlace.properties.Longitude);
      } else {
        url.searchParams.delete('addr');
        url.searchParams.delete('lat');
        url.searchParams.delete('lng');
      }
      // set grade params
      if (gradeFilters.length > 0) {
        url.searchParams.set('selGrades', gradeFilters.toString(","));
      } else {
        url.searchParams.delete('selGrades');
      }
      // set ward params
      if (wardFilters.length > 0) {
        url.searchParams.set('selWards', wardFilters.toString());
      } else {
        url.searchParams.delete('selWards');
      }
      // set program params
      if (offeringSorts.length > 0 || inBoundaryFilter) {
        let programIds = [];
        if (offeringSorts.length > 0) {
          programIds = offeringSorts.map(item => {
            return item.id
          });
        }
        if (inBoundaryFilter) {
          programIds.push("9999");
        }
        url.searchParams.set('programs', programIds.toString());
      } else {
        url.searchParams.delete('programs');
      }
      window.history.pushState(null, '', url.toString().replaceAll("%2C",",",));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  function setFilters(paramGrades, paramWards, paramOfferings, paramAddress) {
    // take query params and set as initial filtered values
    let gradeArray = gradeFilters;
    let wardArray = wardFilters;
    let inBoundSet = inBoundaryFilter;
    let offerings = offeringSorts;
    if (paramGrades) {
        gradeArray = paramGrades.split(",");
        setGradeFilters(gradeArray);
    }
    if (paramWards) {
        wardArray = paramWards.split(",");
        setWardFilters(wardArray);
    }
    if (paramOfferings) {
        let offeringArray = paramOfferings.split(",");
        // if there's an address and it includes 9999 - this is to show inboundary
        if (offeringArray.includes("9999") && paramAddress) {
          inBoundSet = true;
          setInBoundaryFilter(true);
        }
        offerings = offeringLabels.filter(item => {
          item.value = offeringArray.includes(item.id.toString());
          return offeringArray.includes(item.id.toString());
        });
        setOfferingSorts(offerings);
    }
    return [gradeArray, wardArray, offerings, inBoundSet];
  }

  return (
    <appContext.Provider value={{originSchools, setOriginSchools, schools, setSchools, 
                                  stations, gradeFilters, setGradeFilters, wardFilters, setWardFilters, 
                                  offeringSorts, setOfferingSorts, loadingData, setLoadingData,
                                  selectedSchool, setSelectedSchool, boundaryPolys, setBoundaryPolys,
                                  searchedPlace, setSearchedPlace, inBoundaryFilter, setInBoundaryFilter,
                                  locale, setLocale, filterInit, setFilterInit                           
                                }}>
      <Suspense fallback={<Loading />}>
        <Home />
        <ScrollTop/>
        <Apply />
      </Suspense>
    </appContext.Provider>
  )
}
